import React, { useEffect } from 'react'
import { Organization } from 'interfaces/organization'
import useSubscriptionPreview from 'hooks/use-subscription-preview'
import { formatAmount } from 'utils/organization-utils'
import { formatDate } from 'utils/date-utils'
import InvoiceProductsList from './invoice-products-list'
import useGetOrganizationCustomer from 'hooks/use-get-organization-customer'
import { usePaymentMethods } from 'hooks/use-payment-methods'
import { CustomerAddress } from 'interfaces/stripe'
import { LINQ_ADDRESS } from 'constants/linq-info'

interface InvoicePreviewProps {
  org: Organization
  updatedSubscriptionItems?: any[]
  resetBillingInterval?: boolean
  prorationSettings?: {
    enabled: boolean
    behavior: 'next_invoice' | 'immediately' | 'none'
  }
  originalSubscriptionItems?: any[]
  trialDays?: number | null
  metadata?: Array<{ key: string; value: string }>
  selectedPaymentMethodId?: string | null
  paymentDueDays?: number
  coupons?: any[]
  onErrorChange?: (hasError: boolean) => void
  onPreviewDataChange?: (data: any) => void
}

const formatAddress = (
  address: CustomerAddress,
  name?: string,
  email?: string,
  phone?: string
) => {
  if (!address) return null

  const lines = []
  if (name) lines.push(name)
  if (address.line1) lines.push(address.line1)
  if (address.line2) lines.push(address.line2)

  const cityStateZip = [address.city, address.state, address.postal_code]
    .filter(Boolean)
    .join(', ')
  if (cityStateZip) lines.push(cityStateZip)

  if (address.country) lines.push(address.country)
  if (email) lines.push(email)
  if (phone) lines.push(phone)

  return lines
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  org,
  updatedSubscriptionItems = [],
  resetBillingInterval = false,
  prorationSettings,
  originalSubscriptionItems = [],
  trialDays,
  metadata = [],
  selectedPaymentMethodId,
  paymentDueDays,
  coupons = [],
  onErrorChange,
  onPreviewDataChange,
}) => {
  const {
    mutate: previewSubscription,
    data: previewData,
    isLoading,
    error,
  } = useSubscriptionPreview()
  const { data: customerData, isLoading: customerLoading } =
    useGetOrganizationCustomer({
      organizationId: org.id,
      enabled: !!org.id,
    })
  const { data: paymentMethods, isLoading: paymentMethodLoading } =
    usePaymentMethods({
      organizationId: org.id,
    })

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(!!error)
    }
  }, [error, onErrorChange])

  useEffect(() => {
    if (previewData && onPreviewDataChange) {
      onPreviewDataChange(previewData)
    }
  }, [previewData, onPreviewDataChange])

  const selectedPaymentMethod = paymentMethods?.find(
    (pm) => pm.id === selectedPaymentMethodId
  )
  const billingAddress =
    selectedPaymentMethod?.billing_details?.address ||
    customerData?.billing_address
  const billingName =
    selectedPaymentMethod?.billing_details?.name ||
    customerData?.name ||
    org.name
  const billingEmail =
    selectedPaymentMethod?.billing_details?.email || customerData?.email
  const billingPhone =
    selectedPaymentMethod?.billing_details?.phone || customerData?.phone

  useEffect(() => {
    if (org.id && updatedSubscriptionItems.length > 0) {
      const items = updatedSubscriptionItems.map((item) => ({
        price: item.price_id,
        quantity: item.quantity,
      }))
      const couponIds = (coupons || [])
        .map((c: any) => c?.couponDetails?.id || c?.id)
        .filter(Boolean)

      previewSubscription({
        organizationId: Number(org.id),
        subscription: {
          items,
          discounts: couponIds.map((id: string) => ({ coupon: id })),
        },
        resetBillingInterval,
        prorationSettings,
        trialDays: trialDays || 0,
      })
    }
  }, [
    org.id,
    updatedSubscriptionItems,
    resetBillingInterval,
    prorationSettings,
    trialDays,
    previewSubscription,
    coupons,
  ])

  const handlePreviewClick = () => {
    if (org.id && updatedSubscriptionItems.length > 0) {
      const items = updatedSubscriptionItems.map((item) => ({
        price: item.price_id,
        quantity: item.quantity,
      }))
      const couponIds = (coupons || [])
        .map((c: any) => c?.couponDetails?.id || c?.id)
        .filter(Boolean)

      previewSubscription({
        organizationId: Number(org.id),
        subscription: {
          items,
          discounts: couponIds.map((id: string) => ({ coupon: id })),
        },
        resetBillingInterval,
        prorationSettings,
        trialDays: trialDays || 0,
      })
    }
  }

  const invoiceNumber = `YC${org.id}${Date.now().toString().slice(-4)}`
  const isEmailInvoiceSelected = selectedPaymentMethodId === null
  const previewChargeDate =
    previewData?.data?.preview?.next_payment_attempt ||
    previewData?.data?.preview?.due_date ||
    Date.now() / 1000
  const displayDueDate =
    isEmailInvoiceSelected && typeof paymentDueDays === 'number'
      ? Math.floor(Date.now() / 1000) + paymentDueDays * 24 * 60 * 60
      : previewChargeDate

  const renderInvoiceHeader = () => (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e6ebf1',
        }}
      >
        <div style={{ fontSize: '20px', fontWeight: '600', color: '#000000' }}>
          Invoice
        </div>
        <div style={{ fontSize: '18px', fontWeight: '600', color: '#635bff' }}>
          linq
        </div>
      </div>

      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7c93' }}>
        <div>Invoice number: {invoiceNumber}</div>
        <div>Date due: {formatDate(previewChargeDate)}</div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '24px',
          marginBottom: '24px',
          fontSize: '13px',
        }}
      >
        <div>
          <div
            style={{ fontWeight: '600', color: '#000000', marginBottom: '8px' }}
          >
            {LINQ_ADDRESS.name}
          </div>
          <div style={{ color: '#6b7c93', lineHeight: '1.4' }}>
            {formatAddress(LINQ_ADDRESS)?.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>

        <div>
          <div
            style={{ fontWeight: '600', color: '#000000', marginBottom: '8px' }}
          >
            Bill to
          </div>
          <div style={{ color: '#6b7c93', lineHeight: '1.4' }}>
            {customerLoading || paymentMethodLoading ? (
              <div style={{ fontStyle: 'italic' }}>
                Loading billing address...
              </div>
            ) : billingAddress ? (
              formatAddress(
                billingAddress,
                billingName,
                billingEmail,
                billingPhone
              )?.map((line, i) => <div key={i}>{line}</div>)
            ) : (
              <div style={{ fontStyle: 'italic' }}>
                {org.name}
                <br />
                No billing address available
              </div>
            )}
          </div>
        </div>

        <div>
          <div
            style={{ fontWeight: '600', color: '#000000', marginBottom: '8px' }}
          >
            Ship to
          </div>
          <div style={{ color: '#6b7c93', lineHeight: '1.4' }}>
            {customerLoading ? (
              <div style={{ fontStyle: 'italic' }}>
                Loading shipping address...
              </div>
            ) : customerData?.shipping_address?.address ? (
              formatAddress(
                customerData.shipping_address.address,
                customerData.shipping_address.name || customerData.name,
                customerData.email,
                customerData.phone
              )?.map((line, i) => <div key={i}>{line}</div>)
            ) : billingAddress ? (
              formatAddress(
                billingAddress,
                billingName,
                billingEmail,
                billingPhone
              )?.map((line, i) => <div key={i}>{line}</div>)
            ) : (
              <div style={{ fontStyle: 'italic' }}>
                {org.name}
                <br />
                No shipping address available
              </div>
            )}
          </div>
        </div>
      </div>

      {previewData?.data?.preview && (
        <div
          style={{
            marginBottom: '24px',
            fontSize: '20px',
            color: '#000000',
            fontWeight: '700',
            textAlign: 'center',
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
          }}
        >
          {formatAmount(previewData.data.preview.amount_due)} USD due{' '}
          {formatDate(displayDueDate)}
        </div>
      )}
    </>
  )

  const renderPreviewDetails = () => {
    if (!previewData?.data?.preview) return null

    const {
      amount_due: amountDue,
      subtotal,
      total,
      starting_balance: startingBalance,
      ending_balance: endingBalance,
      amount_paid: amountPaid,
      lines,
      total_discount_amounts: totalDiscountAmounts,
    } = previewData.data.preview
    const { data: lineItems } = lines

    const appliedBalance = startingBalance || 0
    const appliedAmount = amountPaid || 0
    const isCredit = total < 0

    // Use total_discount_amounts from Stripe response instead of line-level discounts
    // This gives us the actual discount amounts that are being applied
    const discountRows = (totalDiscountAmounts || []).map(
      (d: any, idx: number) => {
        const coupon = (coupons as any[] | undefined)?.[idx]
        const percentText = coupon?.percent_off
          ? `${coupon.percent_off}% off`
          : coupon?.amount_off
          ? `$${((coupon.amount_off as number) / 100).toFixed(2)} off`
          : 'discount'
        const label = coupon?.name
          ? `${coupon.name} (${percentText})`
          : `Discount ${idx + 1} (${percentText})`
        return { label, amount: d.amount as number }
      }
    )

    return (
      <>
        <InvoiceProductsList lineItems={lineItems} />

        <div
          style={{
            paddingTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              width: '280px',
              fontSize: '12px',
              marginRight: '40px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#000000',
                paddingBottom: '4px',
              }}
            >
              <span>Subtotal</span>
              <span>
                {subtotal < 0
                  ? `-${formatAmount(Math.abs(subtotal))}`
                  : formatAmount(subtotal)}
              </span>
            </div>

            {/* Discounts section */}
            {(discountRows || []).map((row: any, i: number) => (
              <div
                key={`${row.label}-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#000000',
                  padding: '6px 0',
                  borderBottom:
                    i === (discountRows?.length || 0) - 1
                      ? 'none'
                      : '1px solid #f0f2f5',
                }}
              >
                <span>{row.label}</span>
                <span>-{formatAmount(row.amount)}</span>
              </div>
            ))}

            {/* Total field - show the actual total (can be negative) */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#000000',
                padding: '6px 0',
              }}
            >
              <span>Total</span>
              <span>
                {total < 0
                  ? `-${formatAmount(Math.abs(total))}`
                  : formatAmount(total)}
              </span>
            </div>

            {/* Applied balance field when total is negative */}
            {total < 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#000000',
                  padding: '6px 0',
                }}
              >
                <span>Applied balance</span>
                <span>{formatAmount(Math.abs(endingBalance))}</span>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#000000',
                fontWeight: '600',
                fontSize: '14px',
                borderTop: '1px solid #e6ebf1',
                paddingTop: '8px',
                marginTop:
                  appliedBalance !== 0 || appliedAmount !== 0 || isCredit
                    ? '4px'
                    : '8px',
              }}
            >
              <span>Amount due</span>
              <span>
                {amountDue < 0
                  ? `-${formatAmount(Math.abs(amountDue))}`
                  : formatAmount(amountDue)}{' '}
                USD
              </span>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e6ebf1',
          boxShadow: '0 10px 15px rgba(0,0,0,0.06), 0 4px 6px rgba(0,0,0,0.05)',
          width: '90%',
          height: '100%',
          minHeight: '70vh',
          maxHeight: 'calc(100vh - 140px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          margin: '0 auto',
        }}
      >
        {renderInvoiceHeader()}
        {isLoading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '20px',
              color: '#6b7c93',
              fontSize: '13px',
            }}
          >
            Loading preview...
          </div>
        ) : error && typeof error === 'object' ? (
          <div
            style={{
              padding: '16px',
              backgroundColor: '#fefce8',
              border: '1px solid #facc15',
              borderRadius: '6px',
              marginBottom: '24px',
              color: '#92400e',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>
              Subscription Preview Error
            </div>
            <div>
              {(error as any).code === 'invoice_upcoming_none' ||
              (error as any).message?.includes('invoice_upcoming_none') ||
              (error as any).message?.includes('No upcoming invoices') ? (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Issue:</strong> No upcoming invoice for this
                  subscription. This typically happens when there are no active
                  recurring items or the subscription is in a state where no
                  future charges are expected.
                </div>
              ) : (error as any).message?.includes('canceled subscription') ? (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Issue:</strong> Cannot preview invoice for a canceled
                  subscription. The subscription has been canceled and there are
                  no upcoming invoices to preview.
                </div>
              ) : (error as any).message?.includes(
                  'at least one active plan'
                ) ? (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Issue:</strong> At least one recurring product must be
                  present in the subscription.
                </div>
              ) : (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Issue:</strong>{' '}
                  {(error as any).message ||
                    'An error occurred while previewing the invoice'}
                </div>
              )}
            </div>
          </div>
        ) : (
          previewData && renderPreviewDetails()
        )}
      </div>
    </div>
  )
}

export default InvoicePreview
